import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { GlobalNav } from "../shared/GlobalNav";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Toaster } from "../ui/toaster";
import { AlertCircle, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProvidersPage = () => {
  const { user, logout, token } = useAuth();
  const { toast } = useToast();
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);
  
  useEffect(() => {
    fetchStripeStatus();
  }, []);
  
  const fetchStripeStatus = async () => {
    try {
      const response = await axios.get(`${API}/stripe/connect/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Stripe status:', error);
    }
  };
  
  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      const response = await axios.post(`${API}/stripe/connect/create-account`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.onboarding_url;
    } catch (error) {
      console.error('Failed to connect Stripe:', error);
      toast({
        title: "Connection Failed",
        description: error.response?.data?.detail || "Failed to connect Stripe account",
        variant: "destructive"
      });
      setConnectingStripe(false);
    }
  };
  
  const handleDisconnectStripe = async () => {
    if (!confirm('Are you sure you want to disconnect your Stripe account? You will no longer be able to receive payments.')) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/stripe/connect/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Disconnected",
        description: "Stripe account disconnected successfully"
      });
      fetchStripeStatus();
    } catch (error) {
      console.error('Failed to disconnect Stripe:', error);
      toast({
        title: "Disconnect Failed",
        description: error.response?.data?.detail || "Failed to disconnect Stripe account",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenStripeDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stripe/connect/dashboard-link`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Failed to open Stripe dashboard:', error);
      toast({
        title: "Error",
        description: "Failed to open Stripe dashboard",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const providers = [
    {
      name: "Stripe",
      description: "Online payment processing for businesses - Required to receive payments from your clients",
      status: stripeStatus?.connected && stripeStatus?.charges_enabled ? "Connected & Active" : 
              stripeStatus?.connected ? "Connected (Setup Incomplete)" : 
              "Not Connected",
      isConnected: stripeStatus?.connected || false,
      isActive: stripeStatus?.charges_enabled || false,
      accountId: stripeStatus?.account_id || "",
      accountName: stripeStatus?.account_name || stripeStatus?.display_name || "",
      onConnect: handleConnectStripe,
      onDisconnect: handleDisconnectStripe,
      onSettings: handleOpenStripeDashboard,
      isStripe: true,
      methods: [
        "Visa", "Mastercard", "American Express", "Discover", "Diners Club", "JCB", "China UnionPay",
        "Apple Pay", "Google Pay", "Link", "Bank Transfer / Pay by Bank"
      ]
    },
    {
      name: "BNPL",
      description: "Buy now, pay later payment solutions",
      status: "Coming Soon",
      isConnected: false,
      isActive: false,
      isComingSoon: true
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="providers" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Payment Providers</h1>
          <p className="text-gray-600 font-body">Connect your Stripe account to start receiving payments from your clients.</p>
        </div>
        
        {stripeStatus?.connected && !stripeStatus?.charges_enabled && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
              <div>
                <h3 className="font-semibold text-yellow-900 mb-1">Complete Your Stripe Setup</h3>
                <p className="text-sm text-yellow-800 mb-2">
                  Your Stripe account is connected but not yet activated. Please complete the onboarding to start receiving payments.
                </p>
                {stripeStatus?.requirements?.currently_due?.length > 0 && (
                  <p className="text-sm text-yellow-800">
                    <strong>Requirements:</strong> {stripeStatus.requirements.currently_due.join(', ')}
                  </p>
                )}
                <Button 
                  onClick={handleConnectStripe}
                  className="mt-3 bg-yellow-600 hover:bg-yellow-700 text-white"
                  disabled={connectingStripe}
                >
                  {connectingStripe ? 'Redirecting...' : 'Complete Setup'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {providers.map((provider, index) => (
            <Card key={index} className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 font-heading">{provider.name}</h3>
                      <Badge 
                        className={
                          provider.isTestProvider 
                            ? "bg-blue-100 text-blue-700"
                            : provider.isActive 
                              ? "bg-green-100 text-green-700" 
                              : provider.isConnected 
                                ? "bg-yellow-100 text-yellow-700"
                                : provider.isComingSoon
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-gray-100 text-gray-700"
                        }
                      >
                        {provider.status}
                      </Badge>
                    </div>
                    <p className="text-gray-600 text-sm font-body mb-2">{provider.description}</p>
                    {provider.methods && (
                      <div className="mt-3" data-testid="stripe-payment-methods">
                        <div className="flex flex-wrap gap-2">
                          {provider.methods.map((method) => (
                            <span
                              key={method}
                              className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                            >
                              {method}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2 italic">
                          Subject to availability
                        </p>
                      </div>
                    )}
                    {provider.apiKey && (
                      <p className="text-xs text-gray-500 font-mono mt-2">{provider.apiKey}</p>
                    )}
                    {provider.isConnected && provider.accountId && !provider.isTestProvider && (
                      <div className="flex flex-col gap-1 mt-2">
                        {provider.accountName && (
                          <p className="text-sm text-gray-700 font-medium font-body">{provider.accountName}</p>
                        )}
                        <p className="text-xs text-gray-500 font-mono">Account ID: {provider.accountId}</p>
                      </div>
                    )}
                  </div>
                  <div className="ml-6">
                    {provider.isTestProvider ? (
                      <div className="flex space-x-2">
                        <Badge className="bg-blue-100 text-blue-700">Active</Badge>
                      </div>
                    ) : provider.isStripe ? (
                      provider.isConnected ? (
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="font-body"
                            onClick={provider.onSettings}
                            disabled={loading}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Dashboard
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700 font-body"
                            onClick={provider.onDisconnect}
                            disabled={loading}
                          >
                            Disconnect
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          className="bg-black hover:bg-gray-800 text-white font-body"
                          onClick={provider.onConnect}
                          disabled={connectingStripe}
                        >
                          {connectingStripe ? 'Connecting...' : 'Connect Stripe'}
                        </Button>
                      )
                    ) : provider.isComingSoon ? (
                      <Button 
                        className="bg-gray-300 text-gray-600 cursor-not-allowed font-body"
                        disabled
                      >
                        Coming Soon
                      </Button>
                    ) : (
                      <Button className="bg-black hover:bg-gray-800 text-white font-body">
                        Connect
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-2 font-heading">Why Connect Stripe?</h3>
          <ul className="text-sm text-blue-800 space-y-2 font-body">
            <li>Receive payments directly from your clients for contracts and projects</li>
            <li>Secure payment processing with bank-level encryption</li>
            <li>Automatic payout to your bank account</li>
            <li>Full payment history and transaction tracking</li>
            <li>Platform fee based on your subscription plan (Standard: 3%, Paify Plus: 2%, Embedded: 2%)</li>
          </ul>
        </div>
      </main>
      <Toaster />
    </div>
  );
};

export default ProvidersPage;
